import { memo, useState } from 'react';
import PropTypes from 'prop-types';
import { useTheme } from '@mui/material/styles';
import { Box, Skeleton } from '@mui/material';

/**
 * Component to render a thumbnail for pictures with dynamic loading states and dimensions.
 *
 * @param {Object} props - The properties passed to the component.
 * @param {string} [props.alt=''] - Alternative text, if the picture can't be displayed.
 * @param {number|string} props.dimensions - The size of the thumbnail.
 * @param {string} props.ext - The file extension of the image.
 * @param {string} props.src - The source URL of the image.
 * @returns {JSX.Element} A React component that displays an image or a loading skeleton.
 */
const PictureThumbnail = ({ alt, dimensions, ext, src }) => {
  const {
    shape: { borderRadiusXs }
  } = useTheme();
  const [loading, setLoading] = useState(true);

  const handleLoad = (e) => {
    adjustThumbnailDimensions(e.target, dimensions);
    setLoading(false);
  };

  // ? The img component needs to be rendered to trigger
  // ? the onLoad event that ends the loading state

  return (
    <>
      {loading && <Skeleton variant="rectangular" width={dimensions} height={dimensions} animation="wave" />}

      <Box
        id={`file-thumbnail-${ext}`}
        component="img"
        src={src}
        alt={alt}
        sx={styleThumbnail(borderRadiusXs, loading)}
        onLoad={handleLoad}
        onError={() => setLoading(false)}
      />
    </>
  );
};

const adjustThumbnailDimensions = (img, dimensions) => {
  if (img.naturalWidth > img.naturalHeight) {
    img.style.width = `${dimensions}px`;
    img.style.height = 'auto';
  } else {
    img.style.height = `${dimensions}px`;
    img.style.width = 'auto';
  }
};

const styleThumbnail = (borderRadiusXs, loading) => ({
  flexShrink: 0,
  objectFit: 'contain',
  borderRadius: borderRadiusXs,
  p: 0.25,
  // Hide picture while loading (still added to the DOM)
  position: loading ? 'absolute' : 'relative',
  top: loading ? 99999 : null,
  left: loading ? 99999 : null,
  visibility: loading ? 'hidden' : 'visible'
});

PictureThumbnail.propTypes = {
  alt: PropTypes.string.isRequired,
  src: PropTypes.string.isRequired,
  ext: PropTypes.string,
  dimensions: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};

export default memo(PictureThumbnail);
